// User types
export interface User {
  id: string;
  login: string;
  role: 'admin' | 'user';
  display_name?: string;
  avatar_base64?: string;
  created_at: string;
}

export interface Session {
  id: string;
  user_id: string;
  expires_at: string;
}

// Exercise types
export interface Exercise {
  id: string;
  user_id: string | null;
  name: string;
  muscle_primary: MuscleGroup;
  muscle_secondary?: MuscleGroup;
  technique?: string;
  archived: boolean;
  is_global?: boolean;
  created_at: string;
}

export type MuscleGroup =
  | 'chest'
  | 'back'
  | 'shoulders'
  | 'biceps'
  | 'triceps'
  | 'forearms'
  | 'abs'
  | 'quads'
  | 'hamstrings'
  | 'glutes'
  | 'calves';

export const MUSCLE_GROUPS: MuscleGroup[] = [
  'chest',
  'back',
  'shoulders',
  'biceps',
  'triceps',
  'forearms',
  'abs',
  'quads',
  'hamstrings',
  'glutes',
  'calves',
];

export const MUSCLE_GROUP_LABELS: Record<MuscleGroup, string> = {
  chest: 'Грудь',
  back: 'Спина',
  shoulders: 'Плечи',
  biceps: 'Бицепс',
  triceps: 'Трицепс',
  forearms: 'Предплечья',
  abs: 'Пресс',
  quads: 'Квадрицепс',
  hamstrings: 'Бицепс бедра',
  glutes: 'Ягодицы',
  calves: 'Икры',
};

// Workout types
export interface Workout {
  id: string;
  user_id: string;
  date: string;
  started_at: string;
  ended_at?: string;
  work_total: number;
  sets_count?: number;
  exercises_count?: number;
  created_at: string;
}

export interface WorkoutWithSets extends Workout {
  sets: SetWithExercise[];
  exercises_summary: ExerciseSummary[];
}

export interface Set {
  id: string;
  workout_id: string;
  exercise_id: string;
  set_order: number;
  weight: number;
  reps: number;
  work: number;
  created_at: string;
}

export interface SetWithExercise extends Set {
  exercise_name: string;
  muscle_primary: MuscleGroup;
}

export interface ExerciseSummary {
  exercise_id: string;
  exercise_name: string;
  muscle_primary: MuscleGroup;
  sets_count: number;
  work_total: number;
}

// Measurement types
export interface Measurement {
  id: string;
  user_id: string;
  date: string;
  weight?: number;
  chest?: number;
  waist?: number;
  hips?: number;
  neck?: number;
  biceps_left?: number;
  biceps_right?: number;
  thigh_left?: number;
  thigh_right?: number;
  note?: string;
  created_at: string;
}

// Analytics types
export interface WeeklyStat {
  id: string;
  user_id: string;
  year: number;
  week: number;
  muscle_group: MuscleGroup;
  work_total: number;
  sets_count: number;
  workouts_count: number;
}

export interface MonthlyStat {
  id: string;
  user_id: string;
  year: number;
  month: number;
  muscle_group: MuscleGroup;
  work_total: number;
  sets_count: number;
  workouts_count: number;
}

export interface WeeklyAnalytics {
  year: number;
  week: number;
  week_start: string;
  week_end: string;
  by_muscle: Record<MuscleGroup, number>;
  sets_by_muscle: Record<MuscleGroup, number>;
  total_work: number;
  total_sets: number;
  workouts_count: number;
}

export interface MonthlyAnalytics {
  year: number;
  month: number;
  month_name: string;
  by_muscle: Record<MuscleGroup, number>;
  total_work: number;
  total_sets: number;
  workouts_count: number;
}

export interface CalendarDay {
  date: string;
  workouts_count: number;
  total_sets: number;
  total_work: number;
}

export interface CalendarData {
  days: CalendarDay[];
  current_streak: number;
  longest_streak: number;
  total_workouts: number;
  this_week: number;
  this_month: number;
}

export interface PersonalRecord {
  exercise_id: string;
  exercise_name: string;
  muscle_primary: MuscleGroup;
  max_weight: number;
  max_weight_date: string;
  max_work_set: number;
  max_work_set_weight: number;
  max_work_set_reps: number;
  max_work_set_date: string;
  total_sets_all_time: number;
  total_work_all_time: number;
  first_workout_date: string;
  last_workout_date: string;
}

// Nutrition types
export interface WaterLog {
  id: string;
  user_id: string;
  date: string;
  glasses: number; // bitmask for 6 glasses
  created_at: string;
  updated_at: string;
}

export type MealType = 'breakfast' | 'lunch' | 'dinner' | 'snack';

export const MEAL_TYPE_LABELS: Record<MealType, string> = {
  breakfast: 'Завтрак',
  lunch: 'Обед',
  dinner: 'Ужин',
  snack: 'Перекус',
};

export interface Meal {
  id: string;
  user_id: string;
  date: string;
  meal_type: MealType;
  name: string;
  calories?: number;
  protein?: number;
  fat?: number;
  carbs?: number;
  note?: string;
  created_at: string;
}

// API response types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}
